import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MediaSelector } from '@/components/cms/exhibit-form/MediaSelector';
import { toast } from 'sonner';

interface LogoSettingsProps {
  showLogo: boolean;
  logoUrl: string;
  saving: boolean;
  onUpdateSetting: (key: string, value: boolean | string | object) => Promise<void>;
}

export function LogoSettings({ showLogo, logoUrl, saving, onUpdateSetting }: LogoSettingsProps) {
  const [showLogoDialog, setShowLogoDialog] = useState(false);

  const handleLogoSelect = async (mediaFile: any) => {
    await onUpdateSetting('logoUrl', mediaFile.url);
    setShowLogoDialog(false);
    toast.success('Logo updated successfully');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
        <div className="space-y-1">
          <Label htmlFor="show-logo" className="text-sm font-medium text-foreground">Museum Logo</Label>
          <p className="text-sm text-muted-foreground">
            Display the museum logo at the top of the page
          </p>
        </div>
        <Switch
          id="show-logo"
          checked={showLogo}
          onCheckedChange={(value) => onUpdateSetting('showLogo', value)}
          disabled={saving}
        />
      </div>
      
      {showLogo && (
        <div className="space-y-4 p-4 bg-background border border-border/40 rounded-lg">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 border-2 border-border rounded-xl overflow-hidden bg-muted/50 flex items-center justify-center shadow-sm">
              <img 
                src={logoUrl} 
                alt="Museum Logo" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = "/lovable-uploads/727626ab-d59a-4f99-aed6-fe8a671929b5.png";
                }}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Current Logo</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Change Logo" to upload a new image or select from media library
              </p>
            </div>
            <Dialog open={showLogoDialog} onOpenChange={setShowLogoDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={saving} className="border-2">
                  Change Logo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Select Museum Logo</DialogTitle>
                </DialogHeader>
                <MediaSelector
                  type="image"
                  language=""
                  onSelect={handleLogoSelect}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      )}
    </div>
  );
}